/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import ajax from 'helpers/ajax'
import { createElement, ReactNode } from 'react'
import { ThunkAction } from 'model/store/thunk'
import { ShopState } from 'helpers/reducers'
import { toast } from 'utils/toastUtils'
import SessionAction from 'store/session/SessionAction'
import { consoleLog } from 'utils/utility'
import { ShopArticleItem } from 'types/entities/ShopArticleItem'
import { ShopArticleItemsCart } from 'types/entities/ShopArticleItemsCart'
import { InfoMessageType } from 'components/InfoMessage'
import i18n from 'helpers/i18n'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { ModalSize } from 'types/enums/ModalSize'

import {
    setAjaxLoading as setCommonAjaxLoading,
    setDebug as setCommonDebug,
} from 'store/common/CommonReducer'
import {
    setBlur as setCommonBlur,
    setHeadbarTitle as setCommonHeadbarTitle,
    openModal as openCommonModal,
    openSecondaryModal as openCommonSecondaryModal,
    setMessage as setCommonMessage,
    setInfoMessage as setCommonInfoMessage,
} from 'store/ui/UiReducer'

export function postSupportMessage(data: {
    name: string
    email: string
    subject: string
    message: string
    driver_id: string | null
}): ThunkAction<Promise<Response>> {
    return async (): Promise<Response> => {
        return await ajax.post('/app/post-support-message', data, {})
    }
}

export function postUnsubscribe(data: {
    email: string
}): ThunkAction<Promise<Response>> {
    return async (): Promise<Response> => {
        return await ajax.post('/app/unsubscribe', data, {})
    }
}

export function fetchAssertPayment(
    assert: string | number,
    token?: string
): ThunkAction<Promise<any>> {
    return (): Promise<any> => {
        let url = '/shop/pay?assert=' + +assert
        if (typeof token !== 'undefined') {
            url += '&token=' + token
        }
        return ajax.get(url, {}).then((r) => {
            return r
        })
    }
}

export function confirmCart(redirectUrl: string): ThunkAction<Promise<any>> {
    return (_dispatch, getState): Promise<any> => {
        const cart: ShopState = getState().shopState
        const profile = getState().sessionState.profile
        const session = getState().sessionState.session
        if (!cart) {
            toast.error('Internal error. Contact support')
            return Promise.reject()
        }
        if (!profile) {
            toast.error('Internal error. Profile missing')
            return Promise.reject()
        }
        if (!session) {
            toast.error('Internal error. Session missing')
            return Promise.reject()
        }

        if (!profile.hall) {
            toast.error('Select your default hall first')
        }
        const data = {
            hall_id: profile.hall.id,
            personTypeItems: {
                adult: 0,
                child: 0,
            },
            articleItems: cart.articleItems,
            return_url:
                location.protocol +
                '//' +
                location.host +
                redirectUrl +
                '?assert=1',
            type: 'online_profile',
        }

        return ajax.post('/shop/pay', data, {}).then((r) => {
            consoleLog(r)
            window.location.href = r.redirect
            return r
        })
    }
}

export function confirmGiftShopArticleItemsCart(
    redirectUrl: string,
    shop_article_items_cart: ShopArticleItemsCart
): ThunkAction<Promise<any>> {
    return (): Promise<any> => {
        const data = {
            hall_id: shop_article_items_cart.hall_id,
            shop_article_items_cart_id: shop_article_items_cart.id,
            driver: shop_article_items_cart.driver,
            return_url:
                location.protocol + '//' + location.host + redirectUrl + '',
            type: 'gift_card',
        }
        return ajax.post('/shop/pay', data, {}).then((r) => {
            consoleLog(r)
            window.location.href = r.redirect
            return r
        })
    }
}

export function confirmShopArticleItemsCart(
    redirectUrl: string,
    shop_article_items_cart_id: number
): ThunkAction<Promise<any>> {
    return (_dispatch, getState): Promise<any> => {
        const shopState = getState().shopState
        const profile = getState().sessionState.profile
        const articleItems = shopState.itemsCart.shopArticleItems.map(
            (articleItem: ShopArticleItem) => {
                return {
                    article_id: articleItem.article_id,
                    quantity: articleItem.quantity,
                }
            }
        )
        const data = {
            hall_id: profile.hall.id,
            shop_article_items_cart_id: shop_article_items_cart_id,
            personTypeItems: {
                adult: 0,
                child: 0,
            },
            articleItems: articleItems,
            return_url:
                location.protocol +
                '//' +
                location.host +
                redirectUrl +
                '?assert=1',
            type: 'online_profile',
        }
        return ajax.post('/shop/pay', data, {}).then((r) => {
            consoleLog(r)
            window.location.href = r.redirect
            return r
        })
    }
}

/**
 * Opens a modal dialog by dispatching actions to set the modal state, update the headbar title, and apply a blur effect. Has optional closeModal callback.
 *
 * @optional closeModal - pass callback to on close modal
 * @param {ReactNode} component - The React component representing the modal content to be rendered.
 * @param {Object} props - The properties to be passed to the modal's component.
 * @param {string|null} [title] - Optional. The title to be displayed in the headbar of the modal. Defaults to null if not provided.
 * @return {ThunkAction<void>} A thunk function that dispatches actions to open the modal and handle related state updates.
 */
export function openModal(
    component: ReactNode,
    props: any,
    title?: string | null
): ThunkAction<void> {
    return (dispatch, getState) => {
        const previousFocusId = getState().ui.modal.props?.returnFocusId
        const activeElement = typeof document !== 'undefined' ? document.activeElement : null
        const returnFocusId = props?.returnFocusId ??
            ((activeElement?.closest('[role="dialog"]') ? previousFocusId : activeElement?.id) || previousFocusId)
        dispatch(setCommonHeadbarTitle(title ?? null))
        dispatch(setCommonBlur(true))
        return dispatch(
            openCommonModal({
                component,
                props: {
                    ...props,
                    returnFocusId,
                },
                isOpen: true,
            })
        )
    }
}

export function closeModal(force?: boolean): ThunkAction<void> {
    return (dispatch, getState) => {
        const modal = getState().ui.modal
        const close = () => {
            dispatch(setCommonBlur(false))
            dispatch(setCommonHeadbarTitle(null))
            dispatch(
                openCommonModal({
                    component: null,
                    props: {returnFocusId: modal.props?.returnFocusId},
                })
            )
        }

        if (!force && modal.props?.requireCloseConfirmation === true) {
            dispatch(
                openCommonSecondaryModal({
                    component: createElement(UiAlertConfirmationModal, {
                        title: i18n.t('Confirmation'),
                        message: i18n.t('Do you want to close the form?'),
                        onConfirm: () => {
                            close()
                            dispatch(closeSecondaryModal(true))
                        },
                        onCancel: () => dispatch(closeSecondaryModal(true)),
                    }),
                    props: { modalSize: ModalSize.Small },
                    title: i18n.t('Confirm'),
                    isOpen: true,
                })
            )
            return
        }

        return close()
    }
}

export function openSecondaryModal(
    component: ReactNode,
    props?: any,
    title?: string | null
): ThunkAction<void> {
    return (dispatch) => {
        return dispatch(
            openCommonSecondaryModal({
                component,
                props,
                title,
                isOpen: true,
            })
        )
    }
}

export function closeSecondaryModal(force?: boolean): ThunkAction<void> {
    return (dispatch, getState) => {
        const secondaryModal = getState().ui.secondaryModal

        const close = () => {
            dispatch(
                openCommonSecondaryModal({
                    component: null,
                })
            )
        }

        if (!force && secondaryModal.props?.requireCloseConfirmation === true) {
            dispatch(
                openCommonSecondaryModal({
                    component: createElement(UiAlertConfirmationModal, {
                        title: i18n.t('Confirmation'),
                        message: i18n.t('Do you want to close the form?'),
                        onConfirm: () => {
                            close()
                            dispatch(closeSecondaryModal(true))
                        },
                        onCancel: () => dispatch(closeSecondaryModal(true)),
                    }),
                    props: { modalSize: ModalSize.Small },
                    title: i18n.t('Confirm'),
                    isOpen: true,
                })
            )
            return
        }

        return close()
    }
}

export function toggleModal(component: ReactNode, props = {}): any {
    return openCommonModal({
        toggle: true,
        component,
        props,
    })
}

export function setAjaxLoading(loading: number, url?: string): any {
    return setCommonAjaxLoading({ loading, url })
}

/**
 * @active
 * @param blur
 */
export function setBlur(blur: boolean): any {
    return setCommonBlur(blur)
}

export function setHeadbarTitle(title?: string | null): any {
    return setCommonHeadbarTitle(title ?? null)
}

export function setError(message: string | ReactNode): any {
    toast.error(message)
    return setCommonMessage({
        message,
        messageType: 'error',
    })
}

export function setDebug(data: any): any {
    return setCommonDebug(data)
}

export function addInfoMessage(
    message: string | null,
    messageType: InfoMessageType,
    children?: ReactNode | null
): any {
    return setCommonInfoMessage({
        message,
        messageType,
        children,
    })
}

export function addFlashMessage(
    message: string | ReactNode,
    messageType: string
): any {
    if (messageType === 'success') {
        toast.success(message)
    } else if (messageType === 'error') {
        toast.error(message)
    } else {
        toast.info(message)
    }

    return setCommonMessage({
        message,
        messageType,
    })
}

export function setSuccess(message: string | ReactNode): any {
    toast.success(message)
    return setCommonMessage({
        message,
        messageType: 'success',
    })
}

/* Set Reservation Result */
export function userCheckin(): ThunkAction<Promise<Response>> {
    return (dispatch): Promise<any> => {
        return ajax.post('/user/checkin').then((r) => {
            dispatch(SessionAction.setCheckin(r.driver))
            return r
        })
    }
}

export function setMessage(message: string | ReactNode, type: string): any {
    if (type === 'success') {
        toast.success(message)
    } else if (type === 'error') {
        toast.error(message)
    } else {
        toast.info(message)
    }

    return setCommonMessage({
        message,
        messageType: type,
    })
}
